"use client";

import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Play,
  CheckCircle2,
  Star,
  Clock,
  Users,
  FileText,
  Download,
  Code,
  MessageCircle,
  Phone,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import Image from "next/image";
import { getCourseDetail } from "@/lib/courses-catalog";
import type { CourseCatalogDetail } from "@/lib/courses-catalog";
import type { PublicCourse } from "@/lib/catalog-api";
import { SITE_PHONE_E164, SITE_PHONE_DISPLAY, whatsappUrl } from "@/lib/site-config";
import { getStudentUser, isStudent } from "@/lib/student-auth";
import { getBackendUrl } from "@/lib/backend-url";
import { useEffect, useState } from "react";

const avatar =
  "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=150&auto=format&fit=crop";

/** Merge API course data over static catalog defaults */
function mergeApiData(
  api: PublicCourse,
  staticCourse: CourseCatalogDetail | null,
) {
  return {
    id:               api.slug || api.id,
    title:            api.name || staticCourse?.title || "",
    subtitle:         api.description || api.subtitle || staticCourse?.subtitle || "",
    rating:           api.rating || staticCourse?.rating || 4.8,
    reviews:          api.reviews || staticCourse?.reviews || 0,
    studentsEnrolled: api.studentsEnrolled || staticCourse?.studentsEnrolled || 0,
    priceMain:        api.priceDisplay || staticCourse?.priceMain || "",
    priceCompareNote: api.priceCompareNote || staticCourse?.priceCompareNote,
    emi:              api.emi ?? staticCourse?.emi ?? null,
    level:            (api.level as CourseCatalogDetail["level"]) || staticCourse?.level || "All Levels",
    duration:         api.duration || staticCourse?.duration || "",
    lastUpdated:      api.lastUpdated || staticCourse?.lastUpdated || "",
    image:            api.thumbnail || staticCourse?.image || "",
    whatYouWillLearn: api.whatYouWillLearn?.length
                        ? api.whatYouWillLearn
                        : staticCourse?.whatYouWillLearn ?? [],
    curriculum:       api.curriculum?.length
                        ? api.curriculum
                        : staticCourse?.curriculum ?? [],
    gstNote:          "All prices exclusive of GST",
    instructor: {
      name:     "Mont2 Faculty Pool",
      role:     "Professional Data Scientists",
      avatar,
      students: "Working pros & students",
      courses:  9,
    },
  };
}

export default function CourseDetailPage() {
  const params = useParams();
  const router = useRouter();
  const id = typeof params?.id === "string" ? params.id : "";

  const staticCourse = id ? getCourseDetail(id) : null;

  // Start with static data immediately (no loading flicker), then merge API data
  const [course, setCourse] = useState(() => staticCourse ?? null);
  const [apiLoaded, setApiLoaded] = useState(false);

  useEffect(() => {
    if (!id) return;
    // Fetch from public catalog API — tries slug first, then searches by id
    fetch(`${getBackendUrl()}/api/v1/catalog/courses?limit=50&sortBy=sortOrder&sortDir=asc`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache, no-store, must-revalidate", "Pragma": "no-cache" },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((payload) => {
        const items: PublicCourse[] = payload?.data?.items ?? [];
        // Match by slug or id
        const found = items.find(
          (c) => c.slug === id || c.id === id || c.slug === id.replace(/_/g, "-")
        );
        if (found) {
          setCourse(mergeApiData(found, staticCourse));
        }
        setApiLoaded(true);
      })
      .catch(() => setApiLoaded(true));
  }, [id]);

  const handleApply = () => {
    const user = getStudentUser();
    if (user && isStudent(user)) {
      router.push(`/courses/${id}/purchase`);
    } else {
      router.push(`/student/login?redirect=${encodeURIComponent(`/courses/${id}/purchase`)}`);
    }
  };

  // If no static course and API not loaded yet — show spinner
  if (!course && !apiLoaded) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-mont-violet" />
        </main>
        <Footer />
      </div>
    );
  }

  // Truly not found
  if (!course) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-32 pb-24 px-6">
          <div className="container mx-auto max-w-lg text-center rounded-2xl border border-border bg-card/40 p-10">
            <h1 className="text-2xl font-bold font-heading mb-3 text-foreground">Course not found</h1>
            <p className="text-muted-foreground mb-6">We couldn&apos;t find a course matching this URL.</p>
            <Link href="/courses">
              <Button className="bg-gradient-primary text-white border-0">Back to all courses</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const waCourse = whatsappUrl(
    `Hi MONT2 Academy — I'm interested in "${course.title}". Please share fees (INR), EMI, and next batch dates.`,
  );

  return (
    <div className="site-page min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pb-24 lg:pb-0">
        <section className="relative pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-20 overflow-hidden bg-background">
          <div className="absolute inset-0 z-0">
            {course.image && (
              <Image
                src={course.image}
                alt=""
                fill
                className="object-cover opacity-10 blur-xl"
                priority
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/95 to-background/60" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-mont-violet/10 via-transparent to-transparent" />
          </div>

          <div className="site-container relative z-10 grid lg:grid-cols-12 gap-8 sm:gap-12 xl:gap-16 items-start">

            {/* LEFT COLUMN */}
            <div className="lg:col-span-7 xl:col-span-8 space-y-16">

              {/* Hero Info */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-wrap items-center gap-3 mb-6"
                >
                  <Badge variant="secondary" className="bg-mont-violet/20 text-mont-violet hover:bg-mont-violet/30 border-mont-violet/20">
                    {course.level}
                  </Badge>
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock size={14} /> {course.duration}
                  </span>
                  {course.lastUpdated && (
                    <span className="text-sm text-muted-foreground hidden sm:flex items-center gap-1">
                      • Updated {course.lastUpdated}
                    </span>
                  )}
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 text-foreground leading-tight font-heading"
                >
                  {course.title}
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-xl text-muted-foreground mb-8 max-w-3xl leading-relaxed"
                >
                  {course.subtitle}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-wrap items-center gap-6"
                >
                  <div className="flex items-center gap-3">
                    <Image
                      src={course.instructor.avatar}
                      alt=""
                      width={48}
                      height={48}
                      className="rounded-full border-2 border-mont-violet"
                    />
                    <div>
                      <p className="font-bold text-foreground text-sm">{course.instructor.name}</p>
                      <p className="text-xs text-muted-foreground">{course.instructor.role}</p>
                    </div>
                  </div>

                  <div className="h-10 w-px bg-border hidden sm:block" />

                  <div className="flex items-center gap-2">
                    <div className="flex text-yellow-400">
                      {[1, 2, 3, 4, 5].map((i) => <Star key={i} fill="currentColor" size={18} />)}
                    </div>
                    <span className="font-bold text-foreground">{course.rating}</span>
                    <span className="text-muted-foreground text-sm">
                      ({course.reviews.toLocaleString()} ratings)
                    </span>
                  </div>

                  <div className="h-10 w-px bg-border hidden sm:block" />

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users size={18} />
                    <span>{course.studentsEnrolled.toLocaleString()} learners guided</span>
                  </div>
                </motion.div>
              </div>

              {/* Program Highlights */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-foreground font-heading">Program Highlights</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: <Play size={20} className="text-mont-violet" />, bg: "bg-mont-violet/10", text: "Instructor-led live format" },
                    { icon: <FileText size={20} className="text-mont-orange" />, bg: "bg-mont-orange/10", text: "Assignments & case studies" },
                    { icon: <Download size={20} className="text-blue-500" />, bg: "bg-blue-500/10", text: "Study resources & guidance" },
                    { icon: <Code size={20} className="text-green-500" />, bg: "bg-green-500/10", text: "Tooling aligned to brochure" },
                  ].map((h, i) => (
                    <div key={i} className="glass-card p-5 rounded-2xl flex items-center gap-4 border border-border/50 bg-card/40">
                      <div className={`w-10 h-10 rounded-full ${h.bg} flex items-center justify-center`}>{h.icon}</div>
                      <span className="font-medium text-foreground text-sm">{h.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What you'll learn */}
              {course.whatYouWillLearn.length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold mb-6 text-foreground font-heading">What you&apos;ll learn</h2>
                  <div className="glass-card p-8 rounded-3xl border border-border/50 bg-card/40 shadow-sm">
                    <div className="grid sm:grid-cols-2 gap-5">
                      {course.whatYouWillLearn.map((item, i) => (
                        <div key={i} className="flex gap-3">
                          <CheckCircle2 className="text-mont-violet flex-shrink-0 mt-0.5" size={20} />
                          <span className="text-foreground/90 text-sm leading-relaxed">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Course outline */}
              {course.curriculum.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <h2 className="text-3xl font-bold text-foreground font-heading">Course outline</h2>
                    <span className="text-muted-foreground text-sm font-medium bg-muted px-4 py-1.5 rounded-full">
                      {course.curriculum.length} modules • {course.duration}
                    </span>
                  </div>
                  <div className="space-y-4">
                    {course.curriculum.map((module, i) => (
                      <div
                        key={i}
                        className="border border-border/60 rounded-2xl overflow-hidden bg-card/40 hover:bg-card/80 hover:border-mont-violet/30 transition-colors shadow-sm"
                      >
                        <div className="p-5 flex items-center justify-between flex-wrap gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-mont-violet/10 flex items-center justify-center text-mont-violet font-bold text-sm shadow-sm">
                              {i + 1}
                            </div>
                            <h4 className="font-bold text-foreground md:text-lg">{module.title}</h4>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground font-medium">
                            <span className="hidden sm:inline bg-background px-3 py-1 rounded-full border border-border/50">{module.lessons} lessons</span>
                            <span className="bg-background px-3 py-1 rounded-full border border-border/50">{module.duration}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: Sticky Pricing Box */}
            <div className="lg:col-span-5 xl:col-span-4 relative mt-12 lg:mt-0">
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 100 }}
                className="lg:sticky lg:top-28 w-full glass-card border border-white/10 rounded-3xl overflow-hidden shadow-[0_30px_100px_-20px_rgba(0,0,0,0.5)] z-20 bg-background/80"
              >
                {course.image && (
                  <div className="relative aspect-video bg-muted group cursor-pointer border-b border-border/50">
                    <Image src={course.image} alt="" fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:bg-mont-violet/80 transition-all duration-300">
                        <Play className="text-white ml-1 w-8 h-8" fill="white" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 pointer-events-none">
                      <span className="bg-mont-violet text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">MONT2 Program</span>
                    </div>
                  </div>
                )}

                <div className="p-6 md:p-8 bg-card/95 backdrop-blur-xl space-y-6">
                  <div className="bg-gradient-to-br from-mont-violet/10 via-background to-mont-orange/5 p-6 rounded-2xl border border-border/60 shadow-inner">
                    <p className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Program Fee</p>
                    <div className="flex flex-wrap items-baseline gap-3 mb-2">
                      <span className="text-4xl lg:text-5xl font-extrabold text-foreground tracking-tight leading-none">{course.priceMain}</span>
                      {course.priceCompareNote && (
                        <span className="text-sm font-medium text-muted-foreground line-through decoration-muted-foreground/50 mb-1">{course.priceCompareNote}</span>
                      )}
                    </div>
                    <p className="text-[11px] font-bold text-mont-orange/80 uppercase tracking-wider">{course.gstNote}</p>
                  </div>

                  {course.emi ? (
                    <div className="rounded-2xl border border-border bg-muted/30 px-5 py-4 text-sm flex items-start gap-3">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center shrink-0">
                        <CheckCircle2 size={12} />
                      </div>
                      <div>
                        <span className="font-semibold text-foreground block mb-0.5">Flexible EMI Available</span>
                        <span className="text-muted-foreground text-xs block">{course.emi} (3 months)</span>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-border bg-muted/30 px-5 py-4 text-sm text-muted-foreground text-center">
                      EMI not applicable for this plan. One-time payment only.
                    </div>
                  )}

                  <div className="space-y-3 pt-2">
                    <Button
                      onClick={handleApply}
                      className="w-full h-14 text-lg font-bold rounded-2xl bg-gradient-primary text-white border-0 hover:scale-[1.02] shadow-[0_10px_30px_-10px_rgba(139,92,246,0.5)] transition-all duration-300"
                    >
                      Apply / Enquire Now
                    </Button>
                    <Link href={waCourse} target="_blank" className="block">
                      <Button variant="outline" className="w-full h-14 text-base font-bold rounded-2xl border-border hover:bg-[#25D366]/10 hover:border-[#25D366]/50 hover:text-[#25D366] transition-all">
                        <MessageCircle className="mr-2 h-5 w-5 text-[#25D366]" />
                        Get Syllabus on WhatsApp
                      </Button>
                    </Link>
                  </div>

                  <div className="pt-6 mt-2 border-t border-border/50 text-center">
                    <p className="text-xs text-muted-foreground mb-3 font-medium">Need immediate assistance?</p>
                    <Link href={`tel:${SITE_PHONE_E164}`} className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-foreground hover:text-mont-violet transition-colors bg-muted/50 px-4 py-2 rounded-full">
                      <Phone className="h-4 w-4" />
                      Call Admissions: {SITE_PHONE_DISPLAY}
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-xl p-3 safe-bottom lg:hidden">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Program Fee</p>
            <p className="text-lg font-extrabold text-foreground truncate">{course.priceMain}</p>
          </div>
          <Button onClick={handleApply} className="h-11 px-5 rounded-xl bg-gradient-primary text-white border-0 font-bold text-sm shrink-0">
            Enquire Now
          </Button>
          <Link href={waCourse} target="_blank" className="shrink-0">
            <Button variant="outline" size="icon" className="h-11 w-11 rounded-xl border-border" aria-label="WhatsApp">
              <MessageCircle className="h-5 w-5 text-[#25D366]" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
